import { Box, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { type FC, type PropsWithChildren, useCallback } from 'react';
import { ActionBar } from './ActionBar';
import { ACTION_BAR_HEIGHT } from './constants';
import { LeftSidebar } from './LeftSidebar';
import { RightSidebar } from './RightSidebar';
import type { SidebarWidthProps } from './types';
import usePersistentState from '@hooks/usePersistentState';

type Props = {
    title?: string;
    leftSidebar?: React.ReactNode;
    /** Expanded sidebar width; omitted, the 250px default applies. */
    leftSidebarWidth?: number;
    actionBar?: React.ReactNode;
    rightSidebar?: React.ReactNode;
    isRightSidebarOpen?: boolean;
    rightSidebarWidthProps?: SidebarWidthProps;
    withPadding?: boolean;
    backgroundColor?: string;
};

const Page: FC<PropsWithChildren<Props>> = ({
    title,
    leftSidebar,
    leftSidebarWidth,
    actionBar,
    rightSidebar,
    isRightSidebarOpen = false,
    rightSidebarWidthProps,
    withPadding = true,
    backgroundColor,
    children,
}) => {
    const theme = useMantineTheme();
    const [isLeftSidebarOpen, setIsLeftSidebarOpen] = usePersistentState(
        'sidebar-collapsed',
        true
    );
    const toggleLeftSidebar = useCallback(() => {
        setIsLeftSidebarOpen((prev) => !prev);
    }, [setIsLeftSidebarOpen]);

    /*
     * Below ~1100px the expanded 250px sidebar is a third of the viewport, and
     * it is taken straight out of the grid. Fall back to the 60px icon rail
     * (which already shows hover tooltips, so nothing becomes unreachable).
     *
     * Derived rather than written back, so the user's stored preference is
     * untouched and the sidebar re-expands on its own when the window widens.
     * `useMediaQuery` is `undefined` on first paint — default to expanded so
     * wide screens never flash the rail.
     */
    const isNarrowViewport = useMediaQuery('(max-width: 1100px)') ?? false;
    const isLeftSidebarExpanded = isLeftSidebarOpen && !isNarrowViewport;

    return (
        <>
            {title ? <title>{`${title} - Reparo`}</title> : null}

            <Box
                style={{
                    display: 'flex',
                    height: '100vh',
                    overflow: 'hidden',
                    backgroundColor: backgroundColor,
                }}
            >
                {/* Left Sidebar */}
                {leftSidebar ? (
                    <LeftSidebar
                        isOpen={isLeftSidebarExpanded}
                        onToggle={toggleLeftSidebar}
                        width={leftSidebarWidth}
                    >
                        {leftSidebar}
                    </LeftSidebar>
                ) : null}

                {/* Main Content Area */}
                <Box
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        minWidth: 0, // Important for flex child with overflow
                    }}
                >
                    {/* Action Bar */}
                    {actionBar ? <ActionBar>{actionBar}</ActionBar> : null}

                    {/* Content */}
                    <Box
                        component="main"
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            height: actionBar
                                ? `calc(100vh - ${ACTION_BAR_HEIGHT}px)`
                                : '100vh',
                            padding: withPadding ? theme.spacing.lg : 0,
                            backgroundColor:
                                backgroundColor ?? theme.colors.gray[0],
                        }}
                    >
                        {children}
                    </Box>
                </Box>

                {/* Right Sidebar */}
                {rightSidebar ? (
                    <RightSidebar
                        isOpen={isRightSidebarOpen}
                        widthProps={rightSidebarWidthProps}
                    >
                        {rightSidebar}
                    </RightSidebar>
                ) : null}
            </Box>
        </>
    );
};

export default Page;
